import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { enumPermissions } from "../../consts/management/users/users";
import {
  getLocationsOfReports,
  ReportsLoadingKeys,
  updateLocationsOfReports,
} from "../../slices/reportsSlice";
import {
  formatRelativeDate,
  formatReportDate,
} from "../../features/reports/reportDashboardHelpers";
import {
  ArchiveIcon,
  CloseIcon,
  PinFilledIcon,
  PinIcon,
  PlayIcon,
} from "../../features/reports/reportIcons";
import { ReportLogos } from "../../features/reports/reportLogos";

const accessCategories = Object.entries(enumPermissions).map(([key, label]) => ({
  key,
  label,
}));

function titleCaseLabel(label) {
  return String(label).toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
}

function toggleCategoryDraft(categories, categoryKey) {
  return categories.includes(categoryKey)
    ? categories.filter((entry) => entry !== categoryKey)
    : [...categories, categoryKey];
}

function parseTabName(raw) {
  return String(raw || "").split(",").map((s) => s.trim()).filter(Boolean);
}

function sameAccess(left, right) {
  const a = [...left].sort();
  const b = [...right].sort();
  return a.length === b.length && a.every((value, index) => value === b[index]);
}

export const ReportDetailsDrawer = ({
  isOpen,
  report,
  onClose,
  canEdit,
  isAdmin,
  onOpenReport,
  onTogglePinned,
  onToggleArchived,
}) => {
  const dispatch = useDispatch();
  const { locationsOfReports, loadingKeys, loading } = useSelector((state) => state.reports);
  const [accessDraft, setAccessDraft] = useState([]);

  const reportId = report?.id ?? null;
  const locationRow = useMemo(
    () => (locationsOfReports || []).find((item) => item?.report_name === reportId) ?? null,
    [locationsOfReports, reportId],
  );
  const savedAccess = useMemo(() => parseTabName(locationRow?.tab_name), [locationRow]);

  useEffect(() => {
    if (isOpen) dispatch(getLocationsOfReports({}));
  }, [isOpen, dispatch]);

  useEffect(() => {
    setAccessDraft(savedAccess);
  }, [reportId, savedAccess]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose]);

  const locationsIsLoading = loading && loadingKeys.includes(ReportsLoadingKeys.getLocationsOfReports);
  const updateLocationsIsLoading = loading && loadingKeys.includes(ReportsLoadingKeys.updateLocationsData);
  const dirty = !sameAccess(accessDraft, savedAccess);

  const handleSave = async () => {
    if (!reportId) return;
    try {
      await dispatch(updateLocationsOfReports({
        locations: [{
          report_name: reportId,
          tab_name: accessDraft.length ? accessDraft.join(",") : null,
        }],
      })).unwrap();
      await dispatch(getLocationsOfReports({}));
    } catch (error) {
      console.error(error);
    }
  };

  if (!isOpen || !report) return null;

  const visibilitySummary = accessDraft.length
    ? accessDraft.map((key) => titleCaseLabel(enumPermissions[key] || key)).join(", ")
    : "Everyone";

  return (
    <aside className="fixed inset-0 z-[110] w-full overflow-auto border-[#ececef] bg-white md:static md:z-auto md:w-[380px] md:flex-none md:border-l">
      <div className="px-5 pb-8 pt-[18px]">
        <div className="flex items-start justify-between gap-4">
          <div className="flex min-w-0 items-start gap-3">
            <ReportLogos reportId={report.id} />
            <div className="min-w-0">
              <h2 className="text-[16px] font-[650] tracking-[-.015em] text-[#0f1115]">{report.name}</h2>
              <p className="mt-[3px] text-[12.5px] text-[#7b8090]">{report.description}</p>
            </div>
          </div>
          <button
            type="button"
            aria-label="Close details"
            onClick={onClose}
            className="inline-flex h-7 w-7 flex-none items-center justify-center rounded-[7px] text-[#7b8090] hover:bg-[#f3f3f5]"
          >
            <CloseIcon className="h-[13px] w-[13px]" />
          </button>
        </div>

        <div className="mb-[18px] mt-[14px] flex gap-[18px]">
          <button
            type="button"
            onClick={() => onOpenReport?.(report)}
            className="inline-flex items-center gap-1.5 text-[12.5px] text-[#4b5060] hover:text-[#0f1115]"
          >
            <PlayIcon className="h-[13px] w-[13px]" />
            Open report
          </button>
          <button
            type="button"
            onClick={() => onTogglePinned?.(report)}
            className="inline-flex items-center gap-1.5 text-[12.5px] text-[#4b5060] hover:text-[#0f1115]"
          >
            {report.pinned
              ? <PinFilledIcon className="h-[13px] w-[13px]" />
              : <PinIcon className="h-[13px] w-[13px]" />}
            {report.pinned ? "Unpin" : "Pin"}
          </button>
        </div>

        <dl className="grid grid-cols-[110px_1fr] gap-x-2.5 gap-y-[9px] text-[12.5px]">
          <dt className="text-[#7b8090]">Category</dt>
          <dd className="text-[#0f1115]">
            <span className="inline-flex h-[22px] items-center rounded-md bg-[#f3f3f5] px-2 text-[12px] font-medium text-[#4b5060]">
              {report.category}
            </span>
          </dd>
          <dt className="text-[#7b8090]">Visibility</dt>
          <dd className="text-[#0f1115]">{visibilitySummary}</dd>
          <dt className="text-[#7b8090]">Created by</dt>
          <dd className="text-[#0f1115]">Eadin</dd>
          <dt className="text-[#7b8090]">Last viewed</dt>
          <dd className="text-[#0f1115]">
            {report.lastViewed
              ? `${formatRelativeDate(report.lastViewed)} · ${formatReportDate(report.lastViewed)}`
              : "Never"}
          </dd>
        </dl>

        {canEdit && (
          <section className="mt-[22px]">
            <h3 className="flex items-center justify-between text-[11px] font-semibold uppercase tracking-[.1em] text-[#7b8090]">
              Access
              <span className="text-[12px] font-medium normal-case tracking-normal">
                {accessDraft.length
                  ? `${accessDraft.length} of ${accessCategories.length} groups`
                  : "Everyone"}
              </span>
            </h3>
            <div className="mt-2.5 grid grid-cols-2 gap-x-3.5 gap-y-1.5">
              {accessCategories.map(({ key, label }) => (
                <label
                  key={key}
                  className="flex h-6 cursor-pointer items-center gap-2 text-[12.5px] text-[#4b5060] hover:text-[#0f1115]"
                >
                  <input
                    type="checkbox"
                    checked={accessDraft.includes(key)}
                    disabled={locationsIsLoading}
                    onChange={() => setAccessDraft((current) => toggleCategoryDraft(current, key))}
                    className="h-[15px] w-[15px] rounded accent-[#0f1115]"
                  />
                  {titleCaseLabel(label)}
                </label>
              ))}
            </div>
            {dirty && (
              <div className="mt-2.5 flex items-center justify-between text-[12px] text-[#7b8090]">
                <span>Unsaved access changes</span>
                <span className="flex gap-1.5">
                  <button
                    type="button"
                    onClick={() => setAccessDraft(savedAccess)}
                    className="h-7 rounded-[7px] border border-[#ececef] bg-white px-2.5 text-[12px] text-[#0f1115] hover:bg-[#fafafb]"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={updateLocationsIsLoading}
                    className="h-7 rounded-[7px] bg-[#0f1115] px-2.5 text-[12px] text-white disabled:opacity-50"
                  >
                    Save
                  </button>
                </span>
              </div>
            )}
          </section>
        )}

        {isAdmin && (
          <section className="mt-[22px]">
            <h3 className="flex items-center justify-between text-[11px] font-semibold uppercase tracking-[.1em] text-[#7b8090]">
              Danger zone
            </h3>
            <button
              type="button"
              onClick={() => { onToggleArchived?.(report); onClose?.(); }}
              className="mt-2.5 inline-flex h-7 items-center gap-1.5 rounded-[7px] border border-[#ececef] bg-white px-2.5 text-[12px] text-[#0f1115] hover:bg-[#fafafb]"
            >
              <ArchiveIcon className="h-[13px] w-[13px] text-[#7b8090]" />
              {report.archived ? "Restore" : "Archive report"}
            </button>
          </section>
        )}
      </div>
    </aside>
  );
};
